/**
 * PendingChangesBanner Component
 *
 * This component displays a banner showing pending plan changes for the Pricing Page.
 * It appears when a downgrade is scheduled and won't take effect immediately,
 * showing what changes will occur on the next billing date.
 */

import React, { useState, useEffect } from 'react';
import { Grid, styled } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';

// Styled Components
const BannerContainer = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: 'var(--status-amber-bg)',
  border: '1px solid var(--status-amber-border)',
  borderRadius: '8px',
}));

const HeaderContainer = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
});

const HeaderContent = styled('div')({
  flex: 1,
});

const MainTitle = styled('div')({
  color: 'var(--status-amber-color)',
  fontSize: '0.8rem',
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
});

const SubTitle = styled('div')({
  color: 'var(--status-amber-color)',
  fontSize: '0.7rem',
  marginTop: '2px',
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
  opacity: 0.8,
});

const ChangesContainer = styled(Grid)({
  marginBottom: '16px',
});

const ChangeItem = styled('div')({
  color: 'var(--status-amber-color)',
  fontSize: '0.75rem',
  lineHeight: 1.4,
  marginBottom: '4px',
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
});

const PendingChangesBanner = ({
  pendingChanges,
  nextBillingDate,
  currentPlan,
  originalQuantities,
}) => {
  const [daysDiff, setDaysDiff] = useState(0);

  useEffect(() => {
    // Calculate days until next billing date
    const today = new Date();
    const billingDate = new Date(nextBillingDate);
    const timeDiff = billingDate.getTime() - today.getTime();
    const calculatedDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    setDaysDiff(calculatedDays);
  }, [nextBillingDate]);

  return (
    <BannerContainer container>
      <HeaderContainer container>
        <ScheduleIcon sx={{ color: 'var(--status-amber-color)', fontSize: 16 }} />
        <HeaderContent>
          <MainTitle>Pending Changes</MainTitle>
          <SubTitle>
            Effective{' '}
            {new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }).format(nextBillingDate)}{' '}
            (in {daysDiff} day{daysDiff !== 1 ? 's' : ''})
          </SubTitle>
        </HeaderContent>
      </HeaderContainer>

      {/* Individual Change Lines */}
      <ChangesContainer container direction="column">
        {pendingChanges.plan && pendingChanges.plan !== currentPlan && (
          <ChangeItem>
            • {currentPlan} → {pendingChanges.plan} plan
          </ChangeItem>
        )}
        {pendingChanges.addOns && pendingChanges.addOns.actions < originalQuantities.actions && (
          <ChangeItem>
            • Extra Actions: {originalQuantities.actions} → {pendingChanges.addOns.actions}
          </ChangeItem>
        )}
        {pendingChanges.addOns &&
          pendingChanges.addOns.aiCredits < originalQuantities.aiCredits && (
            <ChangeItem>
              • Extra AI Credits: {originalQuantities.aiCredits} → {pendingChanges.addOns.aiCredits}
            </ChangeItem>
          )}
      </ChangesContainer>
    </BannerContainer>
  );
};

export default PendingChangesBanner;
